import React, { FC, useState } from 'react'
import { Box, Divider } from '@material-ui/core'
import { Title, SvgIcon, NewTabs, Button, ConfirmAction, NotificationType, UserStatus, USER_STATUS, USER_PORTAL_ROLES, ROLES } from 'prace-common-components'
import { MutableUser, OnChangeUserDetails, TabId, tabs, Tabs, TargetField, targetFields, UserDetailsProps as Props } from './types'
import { $Drawer, $DrawerTop, $Container, $Box } from './styles'
import { Details } from './Details'
import { Notes } from './Notes'
import { Bank } from './Bank'
import { ImpersonationActivity } from 'components/ImpersonationActivity'
import { useImpersonateUserMutation } from 'store/api/users'
import { useAppDispatch, useAppSelector } from 'store/hooks'

const IMPERSONATE_DESCRIPTION = 'This opens the user portal in a new browser tab, logged in as this ' +
  'user with a temporary token that expires after 30 minutes. A banner will show that you are ' +
  'impersonating, and every action you take is recorded for auditing. Your admin session is not ' +
  'affected. To end the session, choose "Exit Impersonation" or close the tab.'

/* Only non-admin accounts can be impersonated (the API enforces this too). */
const canImpersonate = (role?: ROLES) => !!role && USER_PORTAL_ROLES.includes(role) && role !== ROLES.SUPER_ADMIN


const assert: <T,>(value: unknown, predicate: boolean, message: string) => asserts value is T =
  (_, predicate, message) => {
    if (!predicate) throw new Error(message)
  }

export const UserDetails: FC<Props> = ({
  open,
  handleClose,
  user,
  onChangeUserDetails,
}) => {
  const [selectedTab, setSelectedTab] = useState<TabId>(Tabs.USER_DETAILS)
  const [mutableUser, setMutableUser] = useState<MutableUser | null>(user)
  const dispatch = useAppDispatch()
  const [impersonateUser] = useImpersonateUserMutation()
  const adminRole = useAppSelector((state) => state.auth.user?.role)
  const isImpersonator = adminRole === ROLES.ADMIN || adminRole === ROLES.EUROHPC_ADMIN
  /* Only admin may edit other users (users/controllers/update.ts), so PRO and EuroHPC Admin get a read-only drawer */
  const readOnly = adminRole !== ROLES.ADMIN
  /* The API leaves bank details out for PRO (users/controllers/get.ts) */
  const visibleTabs = adminRole === ROLES.PRO ? tabs.filter(({ id }) => id !== Tabs.BANK_DETAILS) : tabs

  const handleImpersonate = async () => {
    if (!mutableUser) return
    const portalBase = (PORTAL_URL || '').replace(/\/+$/, '')
    if (!portalBase) {
      dispatch({ type: 'notification', payload: { type: NotificationType.error, msg: 'User portal URL is not configured' } })
      return
    }
    try {
      const { code } = await impersonateUser(mutableUser.id).unwrap()
      window.open(`${portalBase}/#code=${code}`, '_blank', 'noopener')
    } catch (err) {
      console.log(err)
      dispatch({ type: 'notification', payload: { type: NotificationType.error, msg: 'Could not start impersonation session' } })
    }
  }

  // Update mutableUser when user changes from props
  // This is needed because we need to update mutableUser when user changes from props
  // but we do not want to update the user before clicking save
  React.useEffect(() => {
    setMutableUser(user)
  }, [user])
  
  const handleChangeTab = (newTab: number) => {
    assert<TabId>(newTab, Object.values(Tabs).includes(newTab as TabId), `Invalid tab: ${newTab}`)
    setSelectedTab(newTab)
  }
  const handleChange: OnChangeUserDetails<string> = (name, value) => {
    assert<TargetField>(name, Object.values(targetFields).includes(name as TargetField), `Invalid field: ${name}`)
    
    switch (name) {
      case targetFields.USER_REMARKS:
        assert<string>(value, typeof value === 'string', `Invalid user remarks: ${value}`)
        changeUserRemarks(value)
        break
      case targetFields.TAGS:
        assert<string[]>(value, Array.isArray(value), `Invalid tags: ${value}`)
        changeUserTags(value)
        break
      case targetFields.STATUS:
        assert<UserStatus>(value, typeof value === 'string' && Object.values(USER_STATUS).includes(value as UserStatus), `Invalid status: ${value}`)
        changeStatus(value)
        break
    }
  }

  const changeUserTags: OnChangeUserDetails<'tags'> = (values) => {
    setMutableUser((prevState) => {
      if (!prevState) return prevState
      return { ...prevState, tags: values, touched: true }
    })
  }

  const changeUserRemarks: OnChangeUserDetails<'userRemarks'> = (value) => {
    setMutableUser((prevState) => {
      if (!prevState) return prevState
      return { ...prevState, userRemarks: value, touched: true }
    })
  }
  
  const changeStatus: OnChangeUserDetails<'status'> = (value) => {
    setMutableUser((prevState) => {
      if (!prevState) return prevState
      return { ...prevState, status: value, touched: true }
    })
  }

  const handleChangeRole = (role: ROLES) => {
    if (!mutableUser) return
    onChangeUserDetails({
      ...mutableUser,
      role,
    })
  }
  
  const handleSave = () => {
    if (!mutableUser) return

    onChangeUserDetails({
      id: mutableUser.id,
      role: mutableUser.role,
      userRemarks: mutableUser.userRemarks,
      tags: mutableUser.tags,
      status: mutableUser.status,
    })
  }
  
  const close = () => {
    handleClose()
  }
  
  return (
    <$Drawer open={open} anchor='right' onClose={close}>
      {!mutableUser && <Box sx={{ p: 4 }}>Loading...</Box>}
      {mutableUser && (
        <>
          <$DrawerTop>
            <Title>{`${mutableUser.firstName || ''} ${mutableUser.lastName || ''}`}</Title>
            <SvgIcon name='close' size={20} clickable onClick={close} />
          </$DrawerTop>
          {isImpersonator && canImpersonate(mutableUser.role) && (
            <Box px={3} pb={1}>
              <ConfirmAction
                title='Impersonate user'
                question={`Impersonate ${mutableUser.firstName || ''} ${mutableUser.lastName || ''}?`}
                description={IMPERSONATE_DESCRIPTION}
                onConfirm={handleImpersonate}
              >
                <Button variant='outlined' color='primary'>Impersonate user</Button>
              </ConfirmAction>
            </Box>
          )}
          <NewTabs
            tabs={visibleTabs}
            value={visibleTabs.findIndex(({ id }) => id === selectedTab)}
            onChange={(idx) => handleChangeTab(visibleTabs[idx].id)}
          />
          <$Container>
            <Box mb={2}>
              <>
                {selectedTab === Tabs.USER_DETAILS && (
                  <Details user={mutableUser} onChangeRole={handleChangeRole} readOnly={readOnly} />
                )}
                {selectedTab === Tabs.TAGS_AND_NOTES && (
                  <Notes
                    user={mutableUser}
                    readOnly={readOnly}
                    onChangeUserTags={handleChange}
                    onChangeUserRemarks={handleChange}
                    onChangeStatus={handleChange}
                  />
                )}
                {selectedTab === Tabs.BANK_DETAILS && (
                  <Bank user={mutableUser} />
                )}
                {selectedTab === Tabs.HISTORY && (
                  <ImpersonationActivity targetId={mutableUser.id} />
                )}
              </>
            </Box>
            <Divider />
            {!readOnly && selectedTab !== Tabs.USER_DETAILS && <$Box>
              <Button variant='outlined' color='primary' onClick={handleClose}>
                Cancel
              </Button>
              <Button color='primary' disabled={!mutableUser?.touched} onClick={handleSave}>
                Save
              </Button>
            </$Box>}
          </$Container>  
        </>      
      )}
    </$Drawer>
  )
}
